import React, { useState, useEffect } from 'react';
import api from '../api';

const emptyLine = () => ({ account: '', debit: '', credit: '' });

const JournalEntries = () => {
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    description: '',
    lines: [emptyLine(), emptyLine()],
  });

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/journal-entries/'),
      api.get('/accounts/').catch(() => ({ data: [] })),
    ])
      .then(([jRes, aRes]) => {
        setRows(Array.isArray(jRes.data) ? jRes.data : jRes.data.results || []);
        setAccounts(Array.isArray(aRes.data) ? aRes.data : aRes.data.results || []);
      })
      .catch(() => setError('Failed to load journal entries'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const updateLine = (idx, patch) => {
    setForm(prev => {
      const lines = prev.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l));
      return { ...prev, lines };
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const lines = form.lines
        .filter(l => l.account && (Number(l.debit) > 0 || Number(l.credit) > 0))
        .map(l => ({
          account: Number(l.account),
          debit: Number(l.debit || 0),
          credit: Number(l.credit || 0),
        }));
      await api.post('/journal-entries/', {
        date: form.date,
        description: form.description,
        lines,
      });
      setShowModal(false);
      setForm({
        date: new Date().toISOString().slice(0, 10),
        description: '',
        lines: [emptyLine(), emptyLine()],
      });
      load();
    } catch (err) {
      const data = err.response?.data;
      setError(
        data?.detail
        || data?.non_field_errors?.[0]
        || (typeof data === 'object' ? JSON.stringify(data) : null)
        || 'Failed to create entry'
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="page-card"><p className="page-subtitle">Loading journal entries…</p></div>;

  return (
    <div>
      <div className="flex-between">
        <p className="page-subtitle" style={{ margin: 0 }}>
          Double-entry ledger — purchases and distributions also post here automatically.
        </p>
        <button type="button" className="btn btn-primary" onClick={() => setShowModal(true)}>
          New Entry
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      {rows.length === 0 ? (
        <div className="empty-state">
          <strong>No journal entries yet</strong>
          <p>
            Record a purchase or distribute discounts to auto-post, or create a manual entry.
          </p>
          <button type="button" className="btn btn-primary" onClick={() => setShowModal(true)}>
            Create first entry
          </button>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Date</th>
                <th>Created By</th>
                <th>Lines</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const lines = r.lines || [];
                return (
                  <React.Fragment key={r.id}>
                    <tr>
                      <td>{r.description}</td>
                      <td>{r.date}</td>
                      <td>{r.created_by_name || '—'}</td>
                      <td>{lines.length}</td>
                      <td>
                        <button type="button" className="btn btn-outline" onClick={() => toggle(r.id)}>
                          {expanded[r.id] ? 'Hide' : 'Expand'}
                        </button>
                      </td>
                    </tr>
                    {expanded[r.id] && (
                      <tr>
                        <td colSpan={5} style={{ background: '#f8fafc', padding: 0 }}>
                          <table className="table">
                            <thead>
                              <tr>
                                <th>Code</th>
                                <th>Account</th>
                                <th>Debit</th>
                                <th>Credit</th>
                              </tr>
                            </thead>
                            <tbody>
                              {lines.map(line => (
                                <tr key={line.id}>
                                  <td>{line.account_code}</td>
                                  <td>{line.account_name}</td>
                                  <td>{Number(line.debit).toFixed(2)}</td>
                                  <td>{Number(line.credit).toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <h3>New Journal Entry</h3>
            <p className="modal-lead">Debits must equal credits. Closed periods cannot be posted into.</p>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label htmlFor="je-date">Date</label>
                <input id="je-date" type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required />
              </div>
              <div className="form-group">
                <label htmlFor="je-desc">Description</label>
                <input id="je-desc" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="e.g. Manual adjustment" required />
              </div>
              {form.lines.map((line, idx) => (
                <div className="form-row" key={idx}>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Account (line {idx + 1})</label>
                    <select value={line.account} onChange={e => updateLine(idx, { account: e.target.value })} required>
                      <option value="">Select account</option>
                      {accounts.map(a => (
                        <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Debit</label>
                    <input type="number" step="0.01" min="0" value={line.debit} onChange={e => updateLine(idx, { debit: e.target.value, credit: e.target.value ? '' : line.credit })} placeholder="0.00" />
                  </div>
                  <div className="form-group">
                    <label>Credit</label>
                    <input type="number" step="0.01" min="0" value={line.credit} onChange={e => updateLine(idx, { credit: e.target.value, debit: e.target.value ? '' : line.debit })} placeholder="0.00" />
                  </div>
                </div>
              ))}
              <button type="button" className="btn btn-outline" onClick={() => setForm(prev => ({ ...prev, lines: [...prev.lines, emptyLine()] }))}>
                Add line
              </button>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Posting…' : 'Post entry'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default JournalEntries;
